'use client';

import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { updateAccount, fetchUserInfo } from '../redux/slices/dataSlice';
import { AppDispatch, RootState } from '../redux/store';
import ChangePassword from './ChangePassword';
import { useTranslation } from 'react-i18next';
import { logger } from '../utils/logger';

interface UserInfo {
  email: string;
  company: string;
}

export default function AccountBox() {
  const { t } = useTranslation(['account', 'common']);
  const dispatch = useDispatch<AppDispatch>();
  const reduxUserInfo = useSelector((state: RootState) => state.data.userInfo);
  const { isSubAccount } = useSelector((state: RootState) => state.auth);
  const [isEditing, setIsEditing] = useState(false);
  const [userInfo, setUserInfo] = useState<UserInfo>({
    email: '',
    company: ''
  });
  const [editForm, setEditForm] = useState({
    company: ''
  });
  const [showChangePassword, setShowChangePassword] = useState(false);

  useEffect(() => {
    // Fetch user info from server on component mount
    dispatch(fetchUserInfo());
  }, [dispatch]);

  useEffect(() => {
    // Update local state when Redux user info changes
    if (reduxUserInfo) {
      const updatedInfo = {
        email: reduxUserInfo.email || '',
        company: reduxUserInfo.company || ''
      };
      setUserInfo(updatedInfo);
      setEditForm({ company: updatedInfo.company });
    }
  }, [reduxUserInfo]);

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleSave = async () => {
    try {
      await dispatch(updateAccount({ company: editForm.company })).unwrap();
      // Refresh user info from server to get updated data
      await dispatch(fetchUserInfo()).unwrap();
      setIsEditing(false);
    } catch (error) {
      logger.error('Failed to update account:', error);
    }
  };

  const handleCancel = () => {
    setEditForm({ company: userInfo.company });
    setIsEditing(false);
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Email */}
        <div>
          <label className="block text-sm font-medium text-gray-900 mb-1">
            {t('account:emailAddressLabel')}
          </label>
          <div className="p-3 bg-gray-50 border border-gray-200 rounded-md">
            <span className="text-gray-900">{userInfo.email}</span>
          </div>
        </div>

        {/* Company */}
        <div>
          <label className="block text-sm font-medium text-gray-900 mb-1">
            {t('account:company')}
          </label>
          {isEditing ? (
            <input
              type="text"
              value={editForm.company}
              onChange={(e) => setEditForm({ ...editForm, company: e.target.value })}
              className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder={t('account:enterCompanyName')}
            />
          ) : (
            <div className="p-3 bg-gray-50 border border-gray-200 rounded-md">
              <span className="text-gray-900">{userInfo.company || t('account:notSpecified')}</span>
            </div>
          )}
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex justify-between items-center pt-4 border-t border-gray-200">
        <div></div> {/* Empty div to push buttons to the right */}
        <div className="flex space-x-3">
          {isEditing ? (
            <>
              <button
                onClick={handleCancel}
                className="px-4 py-2 bg-gray-200 text-gray-900 rounded-md hover:bg-gray-300 transition-colors cursor-pointer"
              >
                {t('common:cancel')}
              </button>
              <button
                onClick={handleSave}
                className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors cursor-pointer"
              >
                {t('account:saveChanges')}
              </button>
            </>
          ) : (
            <button
              onClick={handleEdit}
              className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors cursor-pointer"
            >
              {t('account:editProfile')}
            </button>
          )}
        </div>
      </div>

      {/* Password Section - Hidden for sub-accounts */}
      {!isSubAccount && (
        <div className="border border-gray-200 rounded-lg p-4">
          <div className="flex justify-between items-center">
            <div>
              <h3 className="text-lg font-medium text-gray-900">{t('account:password')}</h3>
              <p className="text-sm text-gray-800">••••••••</p>
            </div>
            <button
              onClick={() => setShowChangePassword(true)}
              className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors text-sm cursor-pointer"
            >
              {t('account:changePassword')}
            </button>
          </div>
        </div>
      )}
      
      {/* Change Password Modal */}
      {showChangePassword && (
        <ChangePassword onClose={() => setShowChangePassword(false)} />
      )}
    </div>
  );
}
